/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { Field, Form, Formik } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'
import { SearchIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Button, Form as NXUIForm, Theme } from '@nexigendigital/nxui'
import { setSearchTerm } from 'store/slices/shopSlice'
import { OrderType } from 'models/enums'
import { useAppDispatch } from 'store/hooks'
import { regex } from 'helpers/utils'
import { useLocalStorage } from 'helpers/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    type: OrderType
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Search({ type }: Props) {
    const dispatch = useAppDispatch()
    const formRef = useRef(null)
    const [tldList] = useLocalStorage('tld', ['.com.au', '.com'])

    useEffect(() => {
        dispatch(setSearchTerm(undefined))
    }, [type, dispatch])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
                domain: '',
                tld: '.com.au'
            }}
            validationSchema={
                Yup.object({
                    domain: Yup.string()
                        .required('Required')
                        .matches(type === 'register' ? regex.domain : regex.domainWithExtension, 'Domain Name is not valid')
                })
            }
            onSubmit={(values, { setSubmitting }) => {
                if (type === 'register') {
                    dispatch(setSearchTerm(`${values.domain}${values.tld}`))
                } else {
                    dispatch(setSearchTerm(values.domain))
                }
                setSubmitting(false)
            }}
        >
            {({ isValid, values, initialValues }) => (
                <StyledSearch>
                    <Field
                        label={'Domain Search'}
                        name={'domain'}
                        type={'text'}
                        prefixes={['www']}
                        component={NXUIForm.InputField}
                    />
                    {type === 'register' &&
                        <Field
                            label={'TLD'}
                            name={'tld'}
                            type={'select'}
                            list={tldList.map((value: string) => {
                                return {
                                    label: value,
                                    value
                                }
                            })}
                            component={NXUIForm.SelectField}
                        />
                    }
                    <SearchButton type={'submit'} active={isValid && values.domain !== initialValues.domain}>
                        <SearchIcon/>
                    </SearchButton>
                </StyledSearch>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledSearch = styled(Form)`
     margin: 10px 20px;
     display: flex;
     align-items: center;
     gap: 10px;

     > div:first-child {
         width: 50%;
     }
    
    > div:nth-child(2) {
        min-width: 140px;
    }

    ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm'])} {
        flex-direction: column;

        > * {
            width: 100%;
        }

        > div:first-child {
            width: 100%;
        }

        gap: 0;
    }
`

const SearchButton = styled(Button)`
    min-width: 88px;
    background-color: ${props => props.active ? props => props.theme['primary--100'] : props => props.theme['gray--100']};
    border: none;
    margin: 0;
    
    &:hover {
        background-color: ${props => props.active ? props => props.theme['primary--200'] : props => props.theme['gray--100']};
    }

    > svg {
        fill: ${props => props.active ? props => props.theme['white--100'] : props => props.theme['icon--100']};
        width: 20px;
        height: 20px;
    }
`
